import { roundDecimal } from './product-bundle-utils';

// Σ of each item's (original × quantity), rounded per item and per accumulation.
const sumBundleSummaryPrice = (products) => (products || []).reduce((sum, product) => {
  const quantity = product?.quantity || 1;
  const original = product?.pricing?.original || 0;
  return roundDecimal(sum + roundDecimal(original * quantity));
}, 0);

/**
 * Sums bundle-level savings from bundlePromotionalAdjustments.
 * Footgun: parentBundleAdjustments (from the parent `product` call) is authoritative on pristine load;
 * once the bundle is modified it goes stale, so callers pass null and per-item adjustments are used.
 * Item-level (untagged) discounts are NOT aggregated here — they're already reflected in each item's
 * price via getItemPrices/getNormalizedItemValue; only bundle-level (tagged) promos are summed.
 * When suppressBundlePromos is true, bundle-level promos are kept out of the advertised total (parked in
 * suppressedBundleSavings so the cart price still reflects them) — see addBundleLevelPromo.
 * @returns {Object} { bundleSavings, suppressedBundleSavings, bundleSavingsByPromos, bundleSummaryPrice }
 */
// When suppressed, the promo keeps its line item (descriptions, rendered as a dash) but goes to
// suppressedBundleSavings instead of bundleSavings, so it's excluded from the advertised total.
const applyBundleLevelPromo = (accumulator, adj, dollarOff, suppressBundlePromos) => {
  const promoId = adj.promoId || 'unknown';
  const { bundleSavingsByPromos } = accumulator;

  if (bundleSavingsByPromos[promoId]) {
    if (!suppressBundlePromos) {
      bundleSavingsByPromos[promoId].savings = roundDecimal(
        bundleSavingsByPromos[promoId].savings + dollarOff
      );
    }
  } else {
    bundleSavingsByPromos[promoId] = {
      promoId,
      savings: suppressBundlePromos ? 0 : dollarOff,
      shortDesc: adj.description?.shortDesc || '',
      longDesc: adj.description?.longDesc || '',
      startDt: adj.dates?.start || null,
      endDt: adj.dates?.end || null,
      ...(suppressBundlePromos ? { suppressed: true } : {})
    };
  }

  if (suppressBundlePromos) {
    accumulator.suppressedBundleSavings = roundDecimal(accumulator.suppressedBundleSavings + dollarOff);
  } else {
    accumulator.bundleSavings = roundDecimal(accumulator.bundleSavings + dollarOff);
  }
};

// Bundle-level promos (BMSM/Exchange) are already the whole-bundle total — use as-is, never × quantity.
const sumTaggedAdjustments = (accumulator, adjustments, suppressBundlePromos) => {
  adjustments.forEach((adj) => {
    if (adj.promotionTag) {
      applyBundleLevelPromo(accumulator, adj, roundDecimal(adj.dollarOff || 0), suppressBundlePromos);
    }
  });
};

export const extractBundleAdjustments = (products, parentBundleAdjustments, suppressBundlePromos = false) => {
  const accumulator = { bundleSavings: 0, suppressedBundleSavings: 0, bundleSavingsByPromos: {} };
  const bundleSummaryPrice = sumBundleSummaryPrice(products);

  // On page load (pristine state), parentBundleAdjustments is the authoritative source from the
  // `product` call. Use it directly instead of per-item adjustments from the `products` call.
  if (parentBundleAdjustments?.length) {
    sumTaggedAdjustments(accumulator, parentBundleAdjustments, suppressBundlePromos);
  } else {
    (products || []).forEach((product) => {
      const adjustments = product?.pricing?.bundlePromotionalAdjustments || [];
      sumTaggedAdjustments(accumulator, adjustments, suppressBundlePromos);
    });
  }

  return { ...accumulator, bundleSummaryPrice };
};
